import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import { EventEmitter, Injectable } from '@angular/core'
import {
  getFilterOnCellContextMenu,
  IssueStatusName,
} from '@helios/ag-grid-config'
import { HighlightingService } from '@helios/generic-components'
import {
  IssueGroup,
  RootState,
  UserPolicy,
  selectUserPolicy,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { environment } from 'environments/environment'
import { Subject } from 'rxjs'
import { filter, take, takeUntil, tap } from 'rxjs/operators'
import { Break } from '../model/break.model'
import {
  BREAK_CODES_COPIED,
  BreakCodeClipboardService,
} from './break-code-clipboard.service'

@Injectable()
export class MultiBreaksContextMenuService {
  showDetailsSelected = new EventEmitter<Break[]>()
  editNoteSelected = new EventEmitter<Break[]>()
  bulkEditSelected = new EventEmitter<Break[]>()
  acceptSelected = new EventEmitter<Break[]>()
  holdSelected = new EventEmitter<{
    breaks: Break[]
    days?: number
  }>()
  unacceptSelected = new EventEmitter<Break[]>()
  createIssueSelected = new EventEmitter<Break[]>()
  viewIssueSelected = new EventEmitter<Break[]>()
  cashBreakSelected = new EventEmitter<Break>()
  cashBreakSettingsSelected = new EventEmitter<Break>()
  breakSelected = new EventEmitter<Break>()
  updateUltClientData = new EventEmitter<{
    ultClientId: string
    ultClientName: string
  }>()

  destroySubject$ = new Subject<void>()

  hasPermissionForDeveloperTools = false
  hasCopilotPermissions = false
  hasPermissionForBulkCwTrans = false
  breakTypesForEmailGeneration = [
    'Coupon Difference',
    'Uncleared Transaction',
    'Position Difference',
    'Trade Factor',
  ]
  ultClientName = ''
  ultClientId = ''

  constructor(
    private readonly highlightService: HighlightingService,
    private readonly store: Store<RootState>,
    private readonly telemetry: TelemetryService,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly toastService: ToastService,
  ) {
    this.init().then()
  }

  async init() {
    await this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        filter((policy): policy is UserPolicy => policy != undefined),
        take(1),
        tap(userPolicy => {
          this.hasCopilotPermissions = userPolicy?.accessCopilot ?? false
          this.hasPermissionForDeveloperTools =
            userPolicy?.accessDeveloperTools ?? false
          this.hasPermissionForBulkCwTrans =
            userPolicy?.accessBulkClearwaterTransactions ?? false
        }),
      )
      .toPromise()

    this.updateUltClientData.subscribe(({ ultClientName, ultClientId }) => {
      this.ultClientName = ultClientName
      this.ultClientId = ultClientId
    })
  }

  openCounterParty(issue: Break) {
    // generate rowId
    const rowId = `${issue.metaData['Cust Counter Party']}__${issue.metaData['Data Source ID']}`

    // generate url of counter party app to redirect
    const url = new URL(environment.externalComponents.counterPartyUrl)
    url.searchParams.set('accountId', issue.acctId.toString())
    // rowIds expects comma separated rowId to support multi row select in counter party app
    url.searchParams.set('rowIds', rowId.toString())

    // navigate user to new tab
    window.open(url.href, '_blank')
  }

  openReconInsightInternalUI(issue: Break) {
    const externalUrl = environment.externalComponents.reconInsightInternalUIUrl
    const issueId = issue.reconInsightIssueId ?? ''
    if (issueId == '') {
      return
    }
    const accountId = issue.acctId.toString()
    const queryParams = new HttpParams()
      .set('accountIds', accountId)
      .set('issueId', issueId)
    const hashParams = 'issues'
    const fullUrl =
      externalUrl + '#' + hashParams + '?' + queryParams.toString()
    window.open(fullUrl, '_blank')
  }

  /** Copy one break's codes + note to the in-app clipboard, ready to paste. */
  copyBreakCodes(brk: Break) {
    this.breakCodeClipboard.copy(brk)
    this.toastService.success(BREAK_CODES_COPIED)
  }

  getContextMenu(
    params: GetContextMenuItemsParams,
    isInCurrentSession?: boolean,
    isSameUltimateParent?: boolean,
  ): Array<string | MenuItemDef> {
    const selectedIssues: Break[] = this.getSelectedIssues(params)
    const moreThanOneIssueSelected = selectedIssues.length > 1
    const { supportedIssueGroup, sameIssueGroup } =
      this.isSameIssueGroup(selectedIssues)
    const unMatchedCounterPartyIssue = selectedIssues.find(
      (issue: Break) => issue.issueGroup === 'UNMATCHED_COUNTER_PARTY',
    )
    const hasReconInsightIssue =
      selectedIssues.filter(issue => issue.reconInsightIssueId != null).length >
      0
    const isCashPositionError = selectedIssues.find(
      (issue: Break) =>
        issue.securityType == 'CASH' &&
        (issue.issueGroup == 'INCORRECT_POSITION' ||
          issue.issueGroup == 'MISSING_EXTERNAL_POSITION'),
    )
    if (this.isGroupSelected(params)) {
      return []
    }

    const aiToolTip: Array<string | MenuItemDef> = []

    if (this.hasCopilotPermissions && window.copilot) {
      aiToolTip.push('separator', {
        name: 'What is this?',
        icon: `<img src="${window.copilot.getIconUrl()}" alt="ClearwaterGPT" class="copilot-logo-context-menu"/>`,
        action: () => {
          window.copilot.open()
          window.copilot.getApplicationChatResponse(
            `What is a ${selectedIssues[0].displayName} break or what is a ${selectedIssues[0].typeName} break?`,
            { dsUsername: 'rubina' },
          )
        },
      })
      if (
        this.hasCopilotPermissions &&
        !moreThanOneIssueSelected &&
        isCashPositionError
      ) {
        aiToolTip.push({
          name: 'Solve Cash Break with AI',
          icon: `<img src="${window.copilot.getIconUrl()}" alt="ClearwaterGPT" class="copilot-logo-context-menu"/>`,
          subMenu: [
            {
              name: 'Advanced Settings ',
              action: () =>
                this.cashBreakSettingsSelected.emit(selectedIssues[0]),
            },
          ],
          action: () => {
            this.cashBreakSelected.emit(selectedIssues[0])
          },
        })
      }
      if (
        (this.hasCopilotPermissions &&
          !moreThanOneIssueSelected &&
          selectedIssues[0] != undefined &&
          this.breakTypesForEmailGeneration.includes(
            selectedIssues[0].displayName,
          )) ||
        (selectedIssues[0]?.metaData?.['CWAN Value'] != null &&
          selectedIssues[0].displayName == 'Equity Dividend')
      ) {
        aiToolTip.push({
          name: 'Generate Email',
          icon: `<img src="${window.copilot.getIconUrl()}" alt="ClearwaterGPT" class="copilot-logo-context-menu"/>`,
          action: () => {
            this.telemetry.emitCustomEvent('heliosBreakEmailGenerate', {
              accountId: selectedIssues[0].acctId,
              breakType: selectedIssues[0].displayName,
            })
            this.breakSelected.emit(selectedIssues[0])
          },
        })
      }
    }

    const results: Array<string | MenuItemDef> = [
      {
        name: 'Show Details',
        disabled: moreThanOneIssueSelected || !isInCurrentSession,
        action: () => this.showDetailsSelected.emit(selectedIssues),
      },
      {
        name: 'Open Counter Party Manager',
        disabled: !unMatchedCounterPartyIssue || moreThanOneIssueSelected,
        action: () => {
          // open counter party app and skip focusing upon other panels in helios
          this.openCounterParty(unMatchedCounterPartyIssue as unknown as Break)
        },
        tooltip: !unMatchedCounterPartyIssue
          ? 'Select only "unmatched counter party" type transaction.'
          : '' || moreThanOneIssueSelected
          ? 'Select only single transaction of unmatched counter party type'
          : '',
      },
      ...aiToolTip,
      'separator',
      {
        name: 'Edit Note',
        icon: '<span class="mdi mdi-note"></span>',
        disabled: selectedIssues.some(
          issue =>
            issue.availableActions == null ||
            !issue.availableActions.includes('EDIT_NOTE'),
        ),
        action: () => this.editNoteSelected.emit(selectedIssues),
      },
      {
        name: 'Copy Break Codes',
        icon: '<span class="mdi mdi-content-copy"></span>',
        disabled: moreThanOneIssueSelected || selectedIssues.length === 0,
        action: () => this.copyBreakCodes(selectedIssues[0]),
        tooltip: moreThanOneIssueSelected
          ? 'Select a single break to copy its break codes'
          : '',
      },
      {
        name: 'Bulk Edit',
        icon: '<span class="mdi mdi-note-multiple-outline"></span>',
        disabled:
          !isSameUltimateParent ||
          !sameIssueGroup ||
          !this.hasPermissionForBulkCwTrans,
        action: () => this.bulkEditSelected.emit(selectedIssues),
        // 4 conditions: same issue group, same ultimate parent, supported issue group, user permission
        tooltip: !isSameUltimateParent
          ? 'All issues should have the same ultimate parent'
          : '' || !supportedIssueGroup
          ? 'Bulk edit is not supported for selected issue type'
          : '' || !sameIssueGroup
          ? 'All issues should be in the same issue group'
          : '' || !this.hasPermissionForBulkCwTrans
          ? 'You do not have permission to bulk edit'
          : '',
      },
      ...getFilterOnCellContextMenu(params),
      'separator',
      {
        name: 'Accept',
        icon: '<span class="mdi mdi-check-circle text-success"></span>',
        disabled:
          selectedIssues.length === 0 ||
          selectedIssues.some(
            issue =>
              issue.availableActions == null ||
              !issue.availableActions.includes('IGNORE'),
          ),
        action: () => this.acceptSelected.emit(selectedIssues),
      },
      {
        name: params.value == IssueStatusName.WAITING ? 'Update Hold' : 'Hold',
        icon: '<span class="mdi mdi-clock-outline"></span>',
        disabled:
          selectedIssues.length === 0 ||
          selectedIssues.some(
            issue =>
              issue.availableActions == null ||
              !issue.availableActions.includes('WAIT'),
          ),
        action: () => this.holdSelected.emit({ breaks: selectedIssues }),
      },
      {
        name:
          params.value == IssueStatusName.WAITING ? 'Remove Hold' : 'Unaccept',
        icon: '<span class="mdi mdi-alert-circle text-danger"></span>',
        disabled:
          selectedIssues.length === 0 ||
          selectedIssues.some(
            issue =>
              issue.availableActions == null ||
              !issue.availableActions.includes('UNRESOLVE'),
          ),
        action: () => this.unacceptSelected.emit(selectedIssues),
      },
      {
        name: 'View Issue',
        disabled:
          moreThanOneIssueSelected ||
          !hasReconInsightIssue ||
          selectedIssues.some(
            issue =>
              issue.availableActions == null ||
              !issue.availableActions.includes('VIEW_RECON_INSIGHT_ISSUE'),
          ),
        action: () => {
          // open new tab with deeplink to RI issue
          this.openReconInsightInternalUI(selectedIssues[0])
        },
      },
      {
        name: 'Create Issue',
        disabled:
          moreThanOneIssueSelected ||
          hasReconInsightIssue ||
          selectedIssues.some(
            issue =>
              issue.availableActions == null ||
              !issue.availableActions.includes('CREATE_RECON_INSIGHT_ISSUE'),
          ),
        action: () => {
          // open form modal
          this.createIssueSelected.emit(selectedIssues)
        },
      },
      'separator',
      ...this.highlightService.createHighlightMenuItems(params),
    ]

    return results
  }

  /** @internal */
  getSelectedIssues(params: GetContextMenuItemsParams): Break[] {
    const isMultipleSelected = params.api.getSelectedNodes().length > 1
    if (isMultipleSelected) {
      return params.api
        .getSelectedNodes()
        .filter(x => !x.group)
        .map(x => (x.data as Break) ?? {})
    }

    const issue = params.node?.data as Break
    if (!issue) {
      return []
    }

    return [issue]
  }

  /** @internal */
  isGroupSelected(params: GetContextMenuItemsParams): boolean {
    return params.node?.group ?? false
  }

  isSameIssueGroup(breaks: Break[]): {
    supportedIssueGroup: boolean
    sameIssueGroup: boolean
  } {
    if (breaks.length === 0) {
      return { supportedIssueGroup: false, sameIssueGroup: true }
    }

    const issueGroups = breaks.map(x => x.displayName)
    const issueGroupSet = new Set(issueGroups)

    const supportedIssueGroup = Object.values(IssueGroup).includes(
      issueGroupSet.values().next().value,
    )
    const sameIssueGroup = issueGroupSet.size === 1

    return { supportedIssueGroup, sameIssueGroup }
  }
}
